import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { EnvironmentData } from '../../../model/environment/environment-data';
import { loadDeliveryForRelease, loadSiriusDelivery } from '../deliveries/deliveries.actions';
import { selectDeliveryGuidOfPipelineReleaseView } from '../routing/routing.selectors';
import * as EnvironmentActions from './environment.actions';
import { selectEnvironmentSlice, selectJiraDataCenterUrl } from './environment.selectors';

@Injectable()
export class EnvironmentEffects {
  private readonly action$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly store$ = inject(Store);

  onLoadEnvironmentData$ = createEffect(() => {
    return this.action$.pipe(
      ofType(EnvironmentActions.loadEnvironmentData),
      mergeMap(() => {
        if (!environment.production) {
          const nonProdEnvironment: EnvironmentData = environment as EnvironmentData; // typing is kind of loose here...
          return of(EnvironmentActions.setEnvironmentData({ environmentData: nonProdEnvironment }));
        }
        return this.http.get<EnvironmentData>('./config').pipe(
          map((environmentData) => EnvironmentActions.setEnvironmentData({ environmentData })),
          catchError((_) => {
            return of(EnvironmentActions.loadEnvironmentDataError());
          }),
          takeUntil(this.action$.pipe(ofType(EnvironmentActions.loadEnvironmentData))),
        );
      }),
    );
  });

  onLoadEnvironmentDataForRelease$ = createEffect(() => {
    return this.action$.pipe(
      ofType(EnvironmentActions.loadEnvironmentDataForRelease),
      mergeMap(() => {
        if (!environment.production) {
          const nonProdEnvironment: EnvironmentData = environment as EnvironmentData; // typing is kind of loose here...
          return of(EnvironmentActions.setEnvironmentDataForRelease({ environmentData: nonProdEnvironment }));
        }
        return this.http.get<EnvironmentData>('./config').pipe(
          map((environmentData) => EnvironmentActions.setEnvironmentDataForRelease({ environmentData })),
          catchError((_) => {
            return of(EnvironmentActions.loadEnvironmentDataForReleaseError());
          }),
          takeUntil(this.action$.pipe(ofType(EnvironmentActions.loadEnvironmentDataForRelease))),
        );
      }),
    );
  });

  onSetEnvironmentDataToLoadJiraUrl$ = createEffect(() => {
    return this.action$.pipe(
      // Effect logic to handle setting Jira URL from iframe message would go here
      ofType(
        EnvironmentActions.setDataFromIFrameMessage,
        EnvironmentActions.setEnvironmentData,
        EnvironmentActions.setEnvironmentDataForRelease,
      ),
      concatLatestFrom(() => this.store$.select(selectEnvironmentSlice)),
      map(([action, environmentSlice]) => ({
        baseUrl: environmentSlice.baseUrl ?? action.environmentData.baseUrl,
        deliveryGuid: environmentSlice.deliveryGuid ?? action.environmentData.deliveryGuid,
      })),
      filter((data) => {
        return !!data.baseUrl && !!data.deliveryGuid;
      }),
      map((data) =>
        EnvironmentActions.loadJiraUiBaseUrl({
          ifBaseUrl: data.baseUrl,
          deliveryGuid: data.deliveryGuid,
        }),
      ),
    );
  });

  onSetEnvironmentDataToLoadSiriusDelivery$ = createEffect(() => {
    return this.action$.pipe(
      // Effect logic to handle setting Jira URL from iframe message would go here
      ofType(EnvironmentActions.setDataFromIFrameMessage, EnvironmentActions.setEnvironmentData),
      filter((action) => !!action.environmentData.baseUrl && !!action.environmentData.deliveryGuid),
      map((action) =>
        loadSiriusDelivery({
          deliveryGuid: action.environmentData.deliveryGuid,
        }),
      ),
    );
  });

  onLoadJiraUiBaseUrl$ = createEffect(() => {
    return this.action$.pipe(
      ofType(EnvironmentActions.loadJiraUiBaseUrl),
      concatLatestFrom(() => this.store$.select(selectJiraDataCenterUrl)),
      // BaseUrl endpoint is not yet deployed in all layers, fallback to data center url
      // make sure that fallback data center url is present in store
      filter(([action, jiraDataCenterUrl]) => !!action.ifBaseUrl && !!action.deliveryGuid && !!jiraDataCenterUrl),
      mergeMap(([action, jiraDataCenterUrl]) => {
        const requestUrl = `https://${action.ifBaseUrl}/zprs/api/jira/uiHostUrl`;
        return this.http.get(requestUrl, { headers: { 'x-delivery-guid': action.deliveryGuid } }).pipe(
          map((response: any) => EnvironmentActions.setJiraUiBaseUrl({ jiraUiBaseUrl: response.UI_HOST_URL })),
          catchError((_) => {
            return of(EnvironmentActions.setJiraUiBaseUrl({ jiraUiBaseUrl: jiraDataCenterUrl }));
            // BaseUrl endpoint is not yet deployed in all layers, fallback to data center url
            // return of(EnvironmentActions.loadJiraUiBaseUrlError());
          }),
          takeUntil(
            this.action$.pipe(
              ofType(EnvironmentActions.loadJiraUiBaseUrl),
              filter((newAction) => newAction.deliveryGuid === action.deliveryGuid),
            ),
          ),
        );
      }),
    );
  });

  public onSetSetEnvironmentDataForReleaseLoadDelivery = createEffect(() => {
    return this.action$.pipe(
      ofType(EnvironmentActions.setEnvironmentDataForRelease),
      concatLatestFrom(() => this.store$.select(selectDeliveryGuidOfPipelineReleaseView)),
      map(([_action, deliveryGuid]) => {
        return loadDeliveryForRelease({ deliveryGuid });
      }),
    );
  });
}
